import React, { useContext, useEffect, useState } from 'react';
import { Container, Box, Button, Tabs, Tab, Stack } from '@mui/material';
import { AuthContext } from '../AuthContext';
import { Navigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useQuery } from 'react-query';
import instance from '../helpers';
import ProfileSlideshow from './components/ProfileSlideshow';
import ProfileVideoList from './components/ProfileVideoList';
import ProfileHero from './components/ProfileHero';
import ProfileSectionCard from './components/ProfileSectionCard';
import ProfileEditorWizard from './components/ProfileEditorWizard';

function formatDate(dateString) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}. ${month}. ${year}`;
}

function formatDateForInput(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const fetchDataCategories = async () => {
  const response = await instance().get(`/profile`);
  return response.data;
};

const getInitialProfileData = (profile) => ({
  name: profile?.name || '',
  birthDate: profile?.birthDate ? formatDateForInput(profile.birthDate) : '',
  deathDate: profile?.deathDate ? formatDateForInput(profile.deathDate) : '',
  biography: profile?.biography || '',
});

function Profile() {
  const [currentTab, setCurrentTab] = useState(0);
  const [edit, setEdit] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [gallery, setGallery] = useState({gallery:[]});
  const [videos, setVideos] = useState({videos:[]});
  const [profileData, setProfileData] = useState({
    name: '',
    birthDate: '',
    deathDate: '',
    biography: '',
  });

  const { enqueueSnackbar } = useSnackbar();
  const { data, status, refetch } = useQuery([], fetchDataCategories, { cacheTime: 0 });
  const { isAuthenticated, verifyToken, role } = useContext(AuthContext);

  const selectedProfile = profiles[currentTab];
  const dateRange = profileData.birthDate && profileData.deathDate
    ? `${formatDate(profileData.birthDate)} – ${formatDate(profileData.deathDate)}`
    : '';

  useEffect(() => {
    verifyToken();
  }, []);
  
  useEffect(() => {
    if (data && data.profiles) {
      setProfiles(data.profiles);
      const profile = data.profiles[currentTab] || data.profiles[0];
      setProfileData(getInitialProfileData(profile));
      setGallery({ gallery: profile?.gallery || [] });
      setVideos({ videos: profile?.videos || [] });
    }
  }, [data, currentTab]);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (role === 'partner') {
    return <Navigate to="/members" replace />;
  }

  if (status === 'error') {
    enqueueSnackbar(`Error fetching data /profiles`, { variant: 'error', autoHideDuration: 1000 });
  }

  const loadProfileIntoForm = (profile) => {
    setProfileData(getInitialProfileData(profile));
    setGallery({ gallery: profile?.gallery || [] });
    setVideos({ videos: profile?.videos || [] });
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setEdit(false);
    loadProfileIntoForm(profiles[newValue]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value,
    });
  };

  const handleSubmit = async (id) => {
    if (!id) return;

    const formattedData = {
      ...profileData,
      ...gallery,
      ...videos,
      birthDate: profileData.birthDate ? new Date(profileData.birthDate).toISOString() : null,
      deathDate: profileData.deathDate ? new Date(profileData.deathDate).toISOString() : null,
    };

    try {
      await instance().put(`/profile/${id}`, formattedData);
      enqueueSnackbar('Profil bol úspešne uložený', { variant: 'success', autoHideDuration: 2000 });
      setEdit(false);
      refetch();
    } catch (error) {
      enqueueSnackbar(`Profil sa nepodarilo uložiť`, { variant: 'error', autoHideDuration: 2000 });
    }
  };

  const handleCancelEdit = () => {
    loadProfileIntoForm(selectedProfile);
    setEdit(false);
  };

  const cb = () => {};

  return (
    <Container component="main" maxWidth="xl" sx={{ marginTop: { xs: '82px', md: '96px' }, pb: 8 }}>
      {profiles.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': { fontWeight: 700, minHeight: 48 },
              '& .Mui-selected': { color: '#182b3a' },
            }}
          >
            {profiles.map((profile, index) => (
              <Tab key={profile?._id || index} label={profile.name} />
            ))}
          </Tabs>
        </Box>
      )}

      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 3, md: 4 } }}>
        {!edit ? (
          <>
            <ProfileHero name={profileData.name} dateRange={dateRange} biography={profileData.biography} />

            <ProfileSectionCard
              title="Fotografie"
              subtitle="Spomienkové fotografie sú zobrazené ako pokojná prezentácia. Kliknutím na fotografiu ju môžete zväčšiť."
              sx={{ maxWidth: 1240 }}
            >
              <ProfileSlideshow itemData={gallery.gallery} />
            </ProfileSectionCard>

            {(videos.videos || []).length > 0 && (
              <ProfileSectionCard
                title="Videá"
                subtitle="Krátke videá zachytávajú dôležité spomienky. Pre prehratie stlačte tlačidlo play priamo vo videu."
                sx={{ maxWidth: 1240 }}
              >
                <ProfileVideoList itemData={videos.videos} showTitle={false} />
              </ProfileSectionCard>
            )}

            {data && data.user && data.user.creator && (
              <Stack direction="row" justifyContent="center" sx={{ width: '100%' }}>
                <Button
                  variant="contained"
                  onClick={() => setEdit(true)}
                  size="large"
                  sx={{
                    color: 'white',
                    minHeight: 52,
                    px: 4,
                    borderRadius: 2,
                    fontWeight: 800,
                    background: '#4866c9',
                  }}
                >
                  Upraviť profil
                </Button>
              </Stack>
            )}
          </>
        ) : (
          <>
            <ProfileHero name={profileData.name} dateRange={dateRange} biography={profileData.biography} mode="edit" />
            <ProfileEditorWizard
              profileId={selectedProfile?._id}
              profileData={profileData}
              gallery={gallery}
              videos={videos}
              setGallery={setGallery}
              setVideos={setVideos}
              handleInputChange={handleInputChange}
              onSave={() => handleSubmit(selectedProfile?._id)}
              onCancel={handleCancelEdit}
              cb={cb}
            />
          </>
        )}
      </Box>
    </Container>
  );
}

export default Profile;
